import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, Injector, Input, NgModule, OnChanges, ViewEncapsulation } from '@angular/core';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { CustomFieldParent, CustomFieldPickerRequest, CustomFieldType, Entity } from '@jms/model';
import { CustomFieldIconPipe } from '@jms/ui/ui.custom.fields.runtime/src/custom.field.icon.pipe';
import { PickerItemModule, PickerItemStyle } from '@jms/ui/ui.picker.item';
import { PickerBaseComponent, PickerFetch } from '@jms/ui/ui.pickers.base';
import { ObservableInput } from '@jms/utils/util.redux';
import { NgSelectModule } from '@ng-select/ng-select';
import * as Enumerable from 'linq';
import { map, Observable, of, withLatestFrom } from 'rxjs';

@Component({
    selector: 'custom-field-picker',
    templateUrl: './custom.field.picker.component.html',
    styleUrls: ['./custom.field.picker.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: CustomFieldPickerComponent, multi: true }
    ],
    standalone: false
})
export class CustomFieldPickerComponent extends PickerBaseComponent implements OnChanges {

  @Input() public data: Entity[];
  @Input() public placeholder = '<Custom Field>';
  @Input() public appendTo;
  @Input() public multiple: boolean;
  @Input() public clearable = true;
  @Input() public showIcon = false;

  @Input() public customRequest?: CustomFieldPickerRequest;

  @ObservableInput() @Input('excludeIds') public excludeIds$: Observable<Entity[]>;

  @Input() public parent = CustomFieldParent.Job;

  /* force trigger change for custom-field-picker #12633 **/
  @Input() public refresh: boolean;

  public data$: Observable<Entity[]>;
  public PickerItemStyle = PickerItemStyle;

  private iconPipe = new CustomFieldIconPipe();

  public get enableGrouping(): boolean {
    return this.customRequest?.parents?.length > 1;
  }

  constructor(
    injector: Injector,
    private http: HttpClient
  ) {
    super(injector);
  }

  public ngOnChanges() {

    this.data$ = this.fetch === PickerFetch.Automatic
      ? this.getCustomFields()
      : of(this.data);

    this.data$ = this.data$
      .pipe(
        withLatestFrom(this.excludeIds$),
        map(([x, excludeIds]) => {
          if (!excludeIds || !excludeIds.length)
            return x;

          const safeData = Enumerable
            .from(x)
            .except(excludeIds, e => e._id)
            .toArray();

          return safeData;

        })
      );

  }

  private getCustomFields(): Observable<Entity[]> {
    // If custom request (assetTypeIds, jobTypeIds, workCategoriesIds) is provided, use it to fetch custom fields
    if (this.customRequest) {
      const request = {
        ...this.customRequest,
        parent: this.parent,
      }
      return this.http.post<Entity[]>(`/api/picker/custom-fields`, request);
    }

    return this.http.get<Entity[]>(`/api/picker/custom-fields/${this.parent}`);
  }

  public searchByGroupFn(term: any, item: any) {
    term = term.toLowerCase();

    // Check if the term matches the group name
    if (item.groupName && item.groupName.toLowerCase().indexOf(term) > -1) {
      return true;
    }

    // If the item has children, search within the children
    if (item.children && item.children.length) {
      return item.children.some(child => child.name.toLowerCase().indexOf(term) > -1);
    }

    // For regular items (non-grouped)
    return item.name.toLowerCase().indexOf(term) > -1;
  }

  public searchFn(term: any, item: any) {
    return super.searchFn(term, item);
  }

  public transformGroupTitle(parent: CustomFieldParent): string {
    switch (parent) {
      case CustomFieldParent.Job:
        return 'Job Fields';
      case CustomFieldParent.Asset:
        return 'Asset Fields';
      case CustomFieldParent.WorkOrder:
        return 'Stage Fields';
      default:
        return '';
    }
  }

  public getIcon(type: CustomFieldType) {
    if (this.showIcon) {
      return `fa ${this.iconPipe.transform(type)}`;
    }

    return null;
  }

}
@NgModule({
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgSelectModule,
    PickerItemModule,
  ],
  declarations: [
    CustomFieldPickerComponent
  ],
  providers: [],
  exports: [
    CustomFieldPickerComponent
  ],
})
export class CustomFieldPickerModule { }